import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import Layout from '@/components/Layout';
import { ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

// A body is "rich" once it contains any of the allowed formatting tags; older plain-text articles
// (newline-separated paragraphs) have none and keep their original paragraph rendering.
const RICH_TAG_RE = /<(p|br|h2|h3|strong|b|em|i|u|ul|ol|li|a)\b/i;
const RTE_TAGS = ['p', 'br', 'h2', 'h3', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'a'];
// Force safe target/rel on links at render time (defense-in-depth; the backend already sanitized).
// The hook is added/removed AROUND this one call so it never leaks into other DOMPurify usage.
const linkHook = (node) => {
  if (node.tagName === 'A' && node.getAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer nofollow');
  }
};
const renderBody = (body) => {
  DOMPurify.addHook('afterSanitizeAttributes', linkHook);
  try {
    return DOMPurify.sanitize(body || '', {
      ALLOWED_TAGS: RTE_TAGS, ALLOWED_ATTR: ['href', 'title', 'target', 'rel'],
    });
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes');
  }
};

export default function EmployerNewsArticle() {
  const [user, setUser] = useState(null);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const navigate = useNavigate();
  const { newsId } = useParams();

  useEffect(() => {
    (async () => {
      try {
        const [u, a] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/regional/news/${newsId}`, { withCredentials: true }),
        ]);
        setUser(u.data); setArticle(a.data);
      } catch (e) {
        try { setUser((await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })).data); } catch { /* ignore */ }
        setNotFound(true);
      } finally { setLoading(false); }
    })();
  }, [newsId]);

  return (
    <Layout user={user}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/employer/news')} data-testid="article-back-news"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to News
        </button>
        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand" /></div>
        ) : notFound || !article ? (
          <div className="text-center py-20" data-testid="article-not-found">
            <p className="text-slate-700 font-medium">Article not found</p>
            <p className="text-sm text-slate-400 mt-1">It may have been removed or is not yet published.</p>
          </div>
        ) : (
          <article data-testid="article-content">
            <p className="text-xs text-slate-400 mb-2">{fmtDate(article.published_at)}</p>
            <h1 className="text-3xl font-bold text-slate-900 leading-tight mb-5">{article.title}</h1>
            {article.featured_image && (
              <div className="w-full rounded-xl overflow-hidden border border-slate-200 mb-6">
                <img src={article.featured_image} alt="" className="w-full max-h-[420px] object-cover" />
              </div>
            )}
            <div className="rte-content max-w-none text-[15px] text-slate-700 leading-relaxed" data-testid="article-body">
              {RICH_TAG_RE.test(article.body || '')
                ? <div dangerouslySetInnerHTML={{ __html: renderBody(article.body) }} />
                : (article.body || '').split('\n').map((para, i) =>
                    para.trim() ? <p key={i} className="mb-4 whitespace-pre-line">{para}</p> : null)}
            </div>
          </article>
        )}
      </div>
    </Layout>
  );
}
